import { useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import {
  NOTIFICATION_PAGE_SIZE,
  useGetNotificationListQuery,
  useGetUnreadCountQuery,
  useMarkAllAsReadMutation,
  useMarkAsReadMutation,
} from "../../../../../store/queries/Notification";

const POLL_INTERVAL_MS = 30000;

const useNotification = () => {
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [page, setPage] = useState(1);

  const { data: unreadCount } = useGetUnreadCountQuery(undefined, {
    pollingInterval: POLL_INTERVAL_MS,
  });

  const { data, isLoading, isFetching, isError, refetch } =
    useGetNotificationListQuery(
      { page, per_page: NOTIFICATION_PAGE_SIZE },
      { skip: !isOpen, refetchOnMountOrArgChange: true },
    );

  const [markAsRead] = useMarkAsReadMutation();
  const [markAllAsRead] = useMarkAllAsReadMutation();

  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);

  // Reset to first page when the dropdown closes so the next open starts fresh
  useEffect(() => {
    if (!isOpen) setPage(1);
  }, [isOpen]);

  const toggleDropdown = (nextOpen) => {
    setIsOpen(nextOpen);
  };

  const handleNotificationClick = async (item) => {
    if (item?.is_read) return;
    const result = await markAsRead(item._id);
    if (result?.error) {
      toast.error("Could not mark notification as read");
    }
  };

  const handleMarkAllAsRead = async () => {
    const result = await markAllAsRead();
    if (result?.error) {
      toast.error("Could not mark all notifications as read");
    }
  };

  const notifications = data?.data ?? [];
  const totalCount = data?.total_count ?? 0;
  const hasMore = notifications.length < totalCount;
  const isInitialLoading = isLoading && notifications.length === 0;
  const isFetchingMore = isFetching && page > 1;

  const loadMore = useCallback(() => {
    if (isFetching || !hasMore) return;
    setPage((p) => p + 1);
  }, [isFetching, hasMore]);

  const displayCount = unreadCount?.count > 99 ? "99+" : unreadCount?.count;
  const hasUnread = unreadCount?.count > 0;

  return {
    notifications,
    totalCount,
    unreadCount: unreadCount?.count,
    displayCount,
    hasUnread,
    isInitialLoading,
    isFetchingMore,
    hasMore,
    loadMore,
    listError: isError,
    isOpen,
    toggleDropdown,
    refetchList: refetch,
    handleNotificationClick,
    handleMarkAllAsRead,
  };
};

export default useNotification;
